import React, { FC, ReactNode } from "react";
import { Map } from "../../../models/Models";
import { Status } from "../../../models/DTOs";
import { ToastContentProps } from "react-toastify";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleRight,
  faCircleXmark,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const resolveType = (variant: Status | null | undefined): Status => {
    return variant ?? "NONE";
};

type ToastVariant = "solid" | "solid-colour" | "outline" | "outline-colour";

const resolveVariant = (variant: ToastVariant | null | undefined): ToastVariant => {
    return variant ?? "solid";
};

interface Props
extends Partial<ToastContentProps> {
    type?: Status;
    variant?: ToastVariant;
    alertData?: {
        heading?: string;
        message?: string;
    }
    children?: ReactNode;
}

const typeVariantClassName = {
    "solid": {
        "INFO":  "text-gray-700 dark:text-gray-200",
        "SUCCESS": "text-gray-700 dark:text-gray-200",
        "WARNING":  "text-gray-700 dark:text-gray-200",
        "ERROR":  "text-gray-700 dark:text-gray-200",
        "NONE":  "text-gray-700 dark:text-gray-200",
    },
    "solid-colour": {
        "INFO":  "text-gray-200 bg-blue-500 dark:bg-blue-800",
        "SUCCESS": "text-gray-200 bg-green-500 dark:bg-green-800",
        "WARNING":  "text-gray-200 bg-amber-500 dark:bg-amber-800",
        "ERROR":  "text-gray-200 bg-red-500 dark:bg-red-800",
        "NONE":  "text-gray-200 bg-gray-500 dark:bg-gray-800",
    },
    "outline": {
        "INFO":  "border border-blue-500 text-gray-700",
        "SUCCESS": "border border-green-500 text-gray-700",
        "WARNING":  "border border-amber-500 text-gray-700",
        "ERROR":  "border border-red-500 text-gray-700",
        "NONE":  "border border-gray-500 text-gray-700",
    },
    "outline-colour": {
        "INFO":  "border border-blue-500 text-gray-700 bg-blue-100 dark:text-gray-200 dark:bg-blue-800",
        "SUCCESS": "border border-green-500 text-gray-700 bg-green-100 dark:text-gray-200 dark:bg-green-800",
        "WARNING":  "border border-amber-500 text-gray-700 bg-amber-100 dark:text-gray-200 dark:bg-amber-800",
        "ERROR":  "border border-red-500 text-gray-700 bg-red-100 dark:text-gray-200 dark:bg-red-800",
        "NONE":  "border border-gray-500 text-gray-700 bg-gray-100 dark:text-gray-200 dark:bg-gray-800",
    },
} as Map<Map<string>>;

const iconTypeVariantClassName = {
    "solid": {
        "INFO":  "text-blue-500 bg-blue-300 dark:text-blue-200 dark:bg-blue-600",
        "SUCCESS": "text-green-500 bg-green-300 dark:text-green-200 dark:bg-green-600",
        "WARNING":  "text-amber-500 bg-amber-300 dark:text-amber-200 dark:bg-amber-600",
        "ERROR":  "text-red-500 bg-red-300 dark:text-red-200 dark:bg-red-600",
        "NONE":  "text-gray-500 bg-gray-300 dark:text-gray-200 dark:bg-gray-600",
    },
    "solid-colour": {
        "INFO":  "text-blue-500 bg-blue-300 dark:text-blue-200 dark:bg-blue-600",
        "SUCCESS": "text-green-500 bg-green-300 dark:text-green-200 dark:bg-green-600",
        "WARNING":  "text-amber-500 bg-amber-300 dark:text-amber-200 dark:bg-amber-600",
        "ERROR":  "text-red-500 bg-red-300 dark:text-red-200 dark:bg-red-600",
        "NONE":  "text-gray-500 bg-gray-300 dark:text-gray-200 dark:bg-gray-600",
    },
    "outline": {
        "INFO":  "text-blue-500 bg-blue-300 dark:text-blue-200 dark:bg-blue-600",
        "SUCCESS": "text-green-500 bg-green-300 dark:text-green-200 dark:bg-green-600",
        "WARNING":  "text-amber-500 bg-amber-300 dark:text-amber-200 dark:bg-amber-600",
        "ERROR":  "text-red-500 bg-red-300 dark:text-red-200 dark:bg-red-600",
        "NONE":  "text-gray-500 bg-gray-300 dark:text-gray-200 dark:bg-gray-600",
    },
    "outline-colour": {
        "INFO":  "text-blue-500 bg-blue-300 dark:text-blue-200 dark:bg-blue-600",
        "SUCCESS": "text-green-500 bg-green-300 dark:text-green-200 dark:bg-green-600",
        "WARNING":  "text-amber-500 bg-amber-300 dark:text-amber-200 dark:bg-amber-600",
        "ERROR":  "text-red-500 bg-red-300 dark:text-red-200 dark:bg-red-600",
        "NONE":  "text-gray-500 bg-gray-300 dark:text-gray-200 dark:bg-gray-600",
    },
} as Map<Map<string>>;


const Toast: FC<Props> = (props: Props) => {

    const resolveThemedClassName = (
        variant: ToastVariant | null | undefined,
        type: Status | null | undefined,
    ): string => {
        const resolvedVariant = resolveVariant(variant);
        const resolvedType = resolveType(type);
        return (
            `${
                typeVariantClassName?.[resolvedVariant]?.[resolvedType] ??
                `text-gray-200 bg-gray-500 dark:bg-gray-800`
            }`
        );
    };

    const baseClassName = (
        variant: ToastVariant | null | undefined,
        type: Status | null | undefined,
    ): string => {
        const resolvedVariant = resolveVariant(variant);
        const resolvedType = resolveType(type);

        return `flex items-center w-full p-4 rounded-lg shadow-sm
                ${resolveThemedClassName(resolvedVariant, resolvedType)}`;
    };

    const resolveIconThemedClassName = (
        variant: ToastVariant | null | undefined,
        type: Status | null | undefined,
    ): string => {
        const resolvedVariant = resolveVariant(variant);
        const resolvedType = resolveType(type);
        return (
            `${
                iconTypeVariantClassName?.[resolvedVariant]?.[resolvedType] ??
                `text-gray-500 bg-gray-300 dark:text-gray-200 dark:bg-gray-600`
            }`
        );
    };

    const baseIconClassName = (
        variant: ToastVariant | null | undefined,
        type: Status | null | undefined,
    ): string => {
        const resolvedVariant = resolveVariant(variant);
        const resolvedType = resolveType(type);

        return `inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-lg
                ${resolveIconThemedClassName(resolvedVariant, resolvedType)}`;
    };

    const resolveIcon = (type: Status | null | undefined): IconProp => {
        switch (type) {
            case "INFO":
                return faCircleInfo;
            case "SUCCESS":
                return faCircleCheck;
            case "WARNING":
                return faCircleExclamation;
            case "ERROR":
                return faCircleXmark;
            case "NONE":
            default:
                return faCircleRight;
        }
    };

    return (
        <div className={baseClassName(props.variant, props.type)}>
            <div className={baseIconClassName(props.variant, props.type)}>
                <FontAwesomeIcon icon={resolveIcon(props.type)} className="w-5 h-5" />
            </div>
            <div className="ms-3">
                { props?.alertData?.heading && <div className="text-sm font-extrabold">{ props.alertData.heading }</div> }
                { props?.alertData?.message && <div className="text-sm font-normal">{ props.alertData.message }</div> }
                { props.children }
            </div>
            <button
                type="button"
                className={`ms-auto -mx-1.5 -my-1.5 p-1.5 rounded-lg inline-flex items-center justify-center h-8 w-8 
                            text-gray-400 hover:text-gray-900 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-gray-300 
                            dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700`}
                onClick={() => props?.closeToast?.("dismiss")}
            >
                <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
            </button>
        </div>

    );
};

export default Toast;